import React from 'react';
import { IPost } from '../../types/resources/Post';
import { useTranslation } from 'react-i18next';
import DateUtils from '../../utils/DateUtils';
import styled from 'styled-components';
import PostStats from './PostStats';
import {ReactComponent as EyeOffIcon} from '../../icons/eye-off.svg';
import {ReactComponent as PlayIcon} from '../../icons/next.svg';
import {ReactComponent as MusicIcon} from '../../icons/music.svg';
import {PostType} from '../../enums/PostType';
import {ImageSize} from '../../enums/ImageSize';
import ImageBlock from '../ImageBlock';

const PostPanel = styled.div`
    display: grid;
    grid-template-columns: 2fr 1fr;
    align-items: start;
    background: var(--cr-white-color);
`;

const PostContent = styled.div`
`;

const MediaButton = styled.a`
    @media (max-width: 768px) {
        && {
            position: relative;
            white-space: normal;
        }
    }
`;

const IconWrapper = styled.span`
    svg {
        height: 1em;
        width: auto;
    }

    @media (max-width: 768px) {
        position: absolute;
        right: 0.5em;
    }
`;

const PostInfo = styled.div`
    background-color: var(--cr-grey-light-color);
    border-top-right-radius: var(--cr-block-rounding);
    border-bottom-right-radius: var(--cr-block-rounding);

    &:before {
        background-color: var(--cr-grey-light-color);
        content: '';
        position: absolute;
        left: 0;
        top: 0;
        height: 100%;
        width: 1rem;
        transform: translateX(-100%);

        mask-image: var(--cr-mask-left);
        mask-position: left;
        mask-repeat: no-repeat;
        mask-size: 100%;
    }
`;

type TProps = {
    post: IPost;
};

export default function PostListItem({ post }: TProps) {
    const { t } = useTranslation();
    const date = DateUtils.getHumanDate(post.createdOn);
    const fullName = [post.authorTitle, post.authorName].join(' ');

    const hasVideo = (post.mediaUrl && (post.type === PostType.MessageWithVideoLink));
    const hasAudio = (post.mediaUrl && (post.type === PostType.MessageWithAudioLink));
    const hasPhoto = (post.mediaUrl && (post.type === PostType.MessageWithPhoto));
    const hasMedia = (hasVideo || hasAudio);

    return (
        <PostPanel className={'cr-shadow cr-gap-lg cr-text-primary cr-rounded-block cr-w-100 cr-spacing cr-justify-between cr-spacing'}>
            <PostContent className={'cr-px-lg cr-h-100 cr-flex'}>
                <div className={'cr-py-xl cr-flex cr-flex-col cr-gap-xl cr-relative'}>
                    <div>
                        {post.isMarkedAsPrivateByAuthor ? t('post-hidden-message') : post.message}
                        {post.isMarkedAsPrivateByAuthor &&
                            <div className={'cr-text-secondary cr-mt-lg'}>
                                <EyeOffIcon/>
                            </div>
                        }
                    </div>
                    {!post.isMarkedAsPrivateByAuthor && hasMedia &&
                        <MediaButton
                            className='cr-pill cr-bg-alert cr-text-white cr-text-base cr-flex cr-gap-lg cr-center cr-justify-between cr-px-lg'
                            href={post.mediaUrl} target="_blank" rel="noreferrer"
                        >
                            {hasVideo && t('watch-video')}
                            {hasAudio && t('listen-to-music')}
                            <IconWrapper className={'cr-ml-auto cr-flex'}>
                                {hasVideo && <PlayIcon/>}
                                {hasAudio && <MusicIcon/>}
                            </IconWrapper>
                        </MediaButton>
                    }
                    {!post.isMarkedAsPrivateByAuthor && hasPhoto &&
                        <ImageBlock
                            src={post.mediaUrl}
                            size={ImageSize.Small}
                            alt={''}
                        />
                    }
                </div>
            </PostContent>
            <PostInfo className={'cr-flex cr-px-lg cr-h-100 cr-relative'}>
                <div className={'cr-flex cr-flex-1 cr-py-xl'}>
                    <div className={'cr-flex cr-flex-col cr-flex-1 cr-justify-between cr-gap-lg cr-relative'}>
                        <div>
                            <div className='cr-bold cr-text-alert cr-pb-sm'>{fullName}</div>
                            <div>{date}</div>
                        </div>
                        {/* TODO: Reactions */}
                        {/*<PostStats post={post}/>*/}
                    </div>
                </div>
            </PostInfo>
        </PostPanel>
    );
};
